import Admin from "../Admin";
import React from "react";
import CardAdminRoleGeneral from "../../../Component/Card/Admin/Role/CardAdminRoleGeneral";
import CardAdminRolePermissions from "../../../Component/Card/Admin/Role/CardAdminRolePermissions";
import CardAdminRoleUsers from "../../../Component/Card/Admin/Role/CardAdminRoleUsers";
import PageHeader from "../../../Component/PageHeader";
import MUIButton from "@mui/material/Button";
import MUIDeleteIcon from "@mui/icons-material/Delete";
import MUISaveButton from "@mui/icons-material/Save";
import MUILoadingButton from "@mui/lab/LoadingButton";
import Menu from "../../../Component/Menu";
import DialogConfirm from "../../../Component/Dialog/DialogConfirm";
import MUIMenuItem from "@mui/material/MenuItem";
import MUIListItemIcon from "@mui/material/ListItemIcon";
import MUITooltip from "@mui/material/Tooltip";
import api from "../../../Seating/Security/api";
import Validator from "../../../Utility/Validator";
import Role from "../../../Utility/Crud/Role";
import { enqueueSnackbar as NotistackEnqueueSnackbar } from "notistack";

export default class AdminRoleCreateEdit extends Admin {
  constructor(props) {
    super(props);

    this.state.role = null;
    this.state.isProcessing = false;
    this.state.isUpdate = !!props.match.params.id;
    this.state.confirmDialogOpen = false;
    this.state.validationIssues = {};
    this.state.selectedPermissionIds = [];
    this.state.originalRolePermissionRows = [];
  }

  /**
   * Set the role in the state.
   */
  afterComponentDidMount() {
    const roleId = this.props.match.params.id
      ? +this.props.match.params.id
      : null;
    if (roleId !== null) {
      const rolePermissionRows = this.getRolePermissionRowsForRole(roleId);
      this.setState({
        role: this.context.rolesIndexed[roleId],
        selectedPermissionIds: rolePermissionRows.map(
          (rolePermission) => rolePermission.permissionId,
        ),
        originalRolePermissionRows: JSON.parse(
          JSON.stringify(rolePermissionRows),
        ),
      });
    }
  }

  getRolePermissionRowsForRole(roleId) {
    const rolePermissions = this.context.rolePermissionsIndexed
      ? Object.values(this.context.rolePermissionsIndexed)
      : [];
    return rolePermissions.filter(
      (rolePermission) => rolePermission.roleId === roleId,
    );
  }

  /**
   * Counts active users assigned to the given role.
   *
   * @param {number} roleId The role ID.
   * @returns {number} Count of active assigned users.
   */
  getActiveUserCountForRole(roleId) {
    const userprofileRoles = this.context.userprofileRolesIndexed
      ? Object.values(this.context.userprofileRolesIndexed)
      : [];
    const assignedUserIds = new Set(
      userprofileRoles
        .filter(
          (userprofileRole) =>
            userprofileRole.roleId === roleId &&
            userprofileRole.deleted !== true,
        )
        .map((userprofileRole) => userprofileRole.userProfileId),
    );

    let activeUserCount = 0;
    assignedUserIds.forEach((userId) => {
      const user = this.context.usersIndexed
        ? this.context.usersIndexed[userId]
        : null;
      if (user && user.active === true) {
        activeUserCount += 1;
      }
    });

    return activeUserCount;
  }

  saveRolePermissionChanges(roleId) {
    const originalRolePermissionRows =
      this.state.originalRolePermissionRows || [];
    const selectedPermissionIds = [
      ...new Set(this.state.selectedPermissionIds || []),
    ];
    const selectedPermissionIdsSet = new Set(
      selectedPermissionIds.map((permissionId) => String(permissionId)),
    );
    const originalPermissionIdsSet = new Set(
      originalRolePermissionRows.map((rolePermission) =>
        String(rolePermission.permissionId),
      ),
    );

    const permissionIdsToAdd = selectedPermissionIds.filter(
      (permissionId) => !originalPermissionIdsSet.has(String(permissionId)),
    );
    const rolePermissionsToRemove = originalRolePermissionRows.filter(
      (rolePermission) =>
        !selectedPermissionIdsSet.has(String(rolePermission.permissionId)),
    );

    const addPromises = permissionIdsToAdd.map((permissionId) =>
      api
        .send("POST", "/rolePermission", {
          roleId: roleId,
          permissionId: permissionId,
        })
        .then((createdRolePermission) => {
          this.context.updateIndexed(
            "rolePermissionsIndexed",
            createdRolePermission,
          );
          return createdRolePermission;
        }),
    );

    const removePromises = rolePermissionsToRemove.map((rolePermission) =>
      api.send("DELETE", `/rolePermission/${rolePermission.id}`).then(() => {
        this.context.deleteIndexed("rolePermissionsIndexed", rolePermission.id);
        return rolePermission;
      }),
    );

    return Promise.all([...addPromises, ...removePromises]).then((results) => {
      const createdRolePermissions = results.slice(0, addPromises.length);
      const retainedRolePermissions = originalRolePermissionRows.filter(
        (rolePermission) =>
          selectedPermissionIdsSet.has(String(rolePermission.permissionId)),
      );
      return retainedRolePermissions.concat(createdRolePermissions);
    });
  }

  /**
   * Creates the role with the current properties in state.role.
   */
  async createRole() {
    this.setState({
      isProcessing: true,
    });

    return api
      .send("POST", "/role", this.state.role)
      .then((role) => {
        this.context.updateIndexed("rolesIndexed", role);
        this.setState({
          role: role,
        });
        return this.saveRolePermissionChanges(role.id).then(
          (nextRolePermissionRows) => ({
            role,
            nextRolePermissionRows,
          }),
        );
      })
      .then(({ nextRolePermissionRows }) => {
        this.setState({
          originalRolePermissionRows: JSON.parse(
            JSON.stringify(nextRolePermissionRows),
          ),
          selectedPermissionIds: nextRolePermissionRows.map(
            (rolePermission) => rolePermission.permissionId,
          ),
        });
        this.props.history.goBack();
        NotistackEnqueueSnackbar("Role created successfully!", {
          variant: "success",
        });
      })
      .catch((err) => {
        console.log(err);
        NotistackEnqueueSnackbar(
          "Oh no! There was an error creating a new Role.",
          { variant: "error" },
        );
        this.setState({
          isProcessing: false,
        });
      });
  }

  /**
   * Updates the role with the current properties in state.role.
   */
  async updateRole() {
    this.setState({
      isProcessing: true,
    });

    return api
      .send("PUT", "/role", this.state.role)
      .then((role) => {
        this.context.updateIndexed("rolesIndexed", role);
        return this.saveRolePermissionChanges(role.id).then(
          (nextRolePermissionRows) => ({
            role,
            nextRolePermissionRows,
          }),
        );
      })
      .then(({ nextRolePermissionRows }) => {
        this.setState({
          originalRolePermissionRows: JSON.parse(
            JSON.stringify(nextRolePermissionRows),
          ),
          selectedPermissionIds: nextRolePermissionRows.map(
            (rolePermission) => rolePermission.permissionId,
          ),
        });
        this.props.history.goBack();
        NotistackEnqueueSnackbar("Role updated successfully!", {
          variant: "success",
        });
      })
      .catch((err) => {
        console.log(err);
        NotistackEnqueueSnackbar(
          "Oh no! There was an error updating the Role.",
          { variant: "error" },
        );
        this.setState({
          isProcessing: false,
        });
      });
  }

  /**
   * Delete the role.
   */
  async deleteRole() {
    this.setState({
      isProcessing: true,
    });

    return api
      .send("DELETE", `/role/${this.state.role.id}`)
      .then(() => {
        this.context.deleteIndexed("rolesIndexed", this.state.role.id);
        this.props.history.goBack();
        NotistackEnqueueSnackbar("Role deleted successfully!", {
          variant: "success",
        });
      })
      .catch((err) => {
        console.log(err);
        NotistackEnqueueSnackbar(
          "Oh no! There was an error deleting the Role.",
          { variant: "error" },
        );
        this.setState({
          confirmDialogOpen: false,
        });
      });
  }

  /**
   * Render the content.
   */
  renderContent() {
    const handleChange = (role) => {
      this.setState({
        role: {
          ...this.state.role,
          ...role,
        },
      });
    };

    return (
      <>
        <CardAdminRoleGeneral
          role={this.state.role}
          onChange={handleChange.bind(this)}
          validationIssues={this.state.validationIssues}
        />
        <CardAdminRolePermissions
          selectedPermissionIds={this.state.selectedPermissionIds}
          onChange={(nextSelectedPermissionIds) => {
            this.setState({
              selectedPermissionIds: nextSelectedPermissionIds,
            });
          }}
        />
        {this.state.isUpdate && this.state.role?.id ? (
          <CardAdminRoleUsers roleId={this.state.role.id} />
        ) : null}
        <DialogConfirm
          open={this.state.confirmDialogOpen}
          onClose={() => this.setState({ confirmDialogOpen: false })}
          onConfirm={() => this.deleteRole()}
          text="Are you sure you want to delete this Role? This cannot be undone."
          header="Delete Role"
          loading={this.state.isProcessing}
        />
      </>
    );
  }

  /**
   * Renders the header.
   */
  renderHeader() {
    let title;
    let subtitle;
    const buttonText = this.state.isUpdate ? "Save" : "Create";

    if (!this.state.isUpdate) {
      title = "Create Role";
      subtitle = null;
    } else {
      title = "Edit Role";
      subtitle =
        this.state.role &&
        Role.getDisplayName(this.state.role) + " • " + this.state.role.id;
    }

    const handleClickBack = () => {
      this.props.history.goBack();
    };

    const handleClickSave = () => {
      const validationIssues = Validator.validate(this.state.role, {
        name: {
          required: true,
        },
      });

      this.setState({
        validationIssues: validationIssues,
      });

      if (Object.keys(validationIssues).length > 0) {
        return NotistackEnqueueSnackbar(
          "Please correct the displayed issues.",
          { variant: "error" },
        );
      }

      if (this.state.isUpdate) {
        this.updateRole();
      } else {
        this.createRole();
      }
    };

    const handleDelete = () => {
      this.setState({
        confirmDialogOpen: true,
      });
    };

    const activeUserCountForRole = this.state.role?.id
      ? this.getActiveUserCountForRole(this.state.role.id)
      : 0;
    const disableDelete = activeUserCountForRole > 0;

    const buttons = [
      <MUIButton onClick={handleClickBack}>Back</MUIButton>,
      <MUILoadingButton
        loading={this.state.isProcessing}
        loadingPosition="start"
        onClick={handleClickSave}
        variant="contained"
        startIcon={<MUISaveButton />}
      >
        {buttonText}
      </MUILoadingButton>,
    ];

    if (this.state.isUpdate) {
      const deleteMenuItem = (
        <MUIMenuItem onClick={handleDelete} disabled={disableDelete}>
          <MUIListItemIcon>
            <MUIDeleteIcon fontSize="small" />
          </MUIListItemIcon>
          Delete
        </MUIMenuItem>
      );

      const menuItems = [
        disableDelete ? (
          <MUITooltip title="Cannot delete a role that has assigned active users.">
            <span>{deleteMenuItem}</span>
          </MUITooltip>
        ) : (
          deleteMenuItem
        ),
      ];
      buttons.push(<Menu menuItems={menuItems} />);
    }

    return <PageHeader title={title} subtitle={subtitle} buttons={buttons} />;
  }
}
